import { useState, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import Header from '../components/Header';
import ProductCard from '../components/ProductCard';
import ProductSkeleton from '../components/ProductSkeleton';
import CategoryFilter from '../components/CategoryFilter';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useSettings } from '../contexts/SettingsContext';
import api from '../lib/api';

const DEFAULT_HERO = {
  enabled: true,
  image_url: 'https://customer-assets.emergentagent.com/job_kits-alimentos-pro/artifacts/irws3z5d_image-15.jpg',
  title: 'Kits TOP de Alimentos',
  subtitle: 'Kits de alimentos para família',
  cta_text: '',
  cta_link: '',
  show_search: true,
  bg_style: 'gradient',
  bg_color: '',
};

const HomePage = () => {
  const { settings, company } = useSettings();
  const hero = { ...DEFAULT_HERO, ...(settings?.hero || {}) };
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  const loadProducts = useCallback(async (reset = false, category = selectedCategory, search = searchQuery) => {
    try {
      setLoading(true);
      const skip = reset ? 0 : page * 20;
      const params = {
        limit: 20,
        skip,
      };
      
      if (category) {
        params.category_id = category;
      }
      if (search) {
        params.search = search;
      }
      
      const response = await api.get('/products', { params });
      
      if (reset) {
        setProducts(response.data);
        setPage(1);
      } else {
        setProducts(prev => [...prev, ...response.data]);
        setPage(prev => prev + 1);
      }
      
      setHasMore(response.data.length === 20);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadCategories();
    loadProducts(true, null, '');
  }, [loadCategories]);

  useEffect(() => {
    loadProducts(true, selectedCategory, searchQuery);
  }, [selectedCategory]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadProducts(true, selectedCategory, searchQuery);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadProducts(false, selectedCategory, searchQuery);
    }
  };

  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          await api.post('/admin/seed');
        }
      } catch (error) {
        // Ignore seed errors
      }
    };
    seedData();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      {hero.enabled && (
      <section
        className={hero.bg_style === 'solid' ? 'text-white py-12 md:py-20' : 'gradient-hero text-white py-12 md:py-20'}
        style={hero.bg_style === 'solid' && hero.bg_color ? { backgroundColor: hero.bg_color } : undefined}
        data-testid="home-hero"
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Logo / Imagem */}
            {hero.image_url && (
              <div className="flex-shrink-0">
                <img
                  src={hero.image_url}
                  alt={hero.title}
                  className="w-64 md:w-80 rounded-2xl shadow-2xl"
                />
              </div>
            )}

            {/* Text Content */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight">
                {hero.title}
              </h1>
              {hero.subtitle && (
                <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-lg">
                  {hero.subtitle}
                </p>
              )}

              {/* Botão CTA (opcional) */}
              {hero.cta_text && hero.cta_link && (
                <a
                  href={hero.cta_link}
                  className="inline-block mb-6 px-6 py-3 rounded-xl bg-secondary text-secondary-foreground font-semibold hover:bg-secondary/90 transition"
                  data-testid="home-hero-cta"
                >
                  {hero.cta_text}
                </a>
              )}

              {/* Search */}
              {hero.show_search && (
              <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto md:mx-0">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="search"
                    placeholder="Buscar produtos ou kits..."
                    className="pl-10 h-12 rounded-xl bg-white border-0"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
                <Button type="submit" className="h-12 px-6 rounded-xl bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="search-button">
                  Buscar
                </Button>
              </form>
              )}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Categories */}
      <section className="py-6 bg-white border-b">
        <div className="container mx-auto px-4">
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        </div>
      </section>

      {/* Products Grid */}
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-foreground">
            {selectedCategory 
              ? categories.find(c => c.id === selectedCategory)?.name || 'Produtos'
              : 'Todos os Produtos'
            }
          </h2>
          <span className="text-sm text-muted-foreground">
            {products.length} {products.length === 1 ? 'produto' : 'produtos'}
          </span>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
          
          {loading && [...Array(4)].map((_, i) => (
            <ProductSkeleton key={`skeleton-${i}`} />
          ))}
        </div>

        {/* Empty State */}
        {!loading && products.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
              <Search className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
              Nenhum produto encontrado
            </h3>
            <p className="text-muted-foreground">
              Tente ajustar seus filtros ou busca
            </p>
          </div>
        )}

        {/* Load More */}
        {hasMore && products.length > 0 && !loading && (
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              onClick={handleLoadMore}
              className="rounded-full"
              data-testid="load-more-button"
            >
              Carregar mais produtos
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">{company?.app_name || 'KITS TOP de ALIMENTOS'}</h3>
              <p className="text-slate-400 text-sm">
                {company?.company_name || 'Comércio Atacadista de Alimentos LTDA'}<br />
                {company?.footer_text || 'Qualidade e economia para sua família.'}
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Links Rápidos</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="/" className="hover:text-white transition-colors">Início</a></li>
                <li><a href="/?category=promocoes" className="hover:text-white transition-colors">Promoções</a></li>
                <li><a href="/meus-pedidos" className="hover:text-white transition-colors">Meus Pedidos</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Contato</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>WhatsApp: {company?.whatsapp || '(11) 99999-9999'}</li>
                <li>Email: {company?.email || 'contato@kitstop.com.br'}</li>
                {company?.address && <li>Endereço: {company.address}</li>}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} {company?.app_name || 'KITS TOP de ALIMENTOS'}. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
